import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, FileText, HelpCircle } from "lucide-react";
import { TrainingTour } from "../tour/TrainingTour";
import { useTrainingTour } from "../tour/useTrainingTour";
import { serviceStandardDetailTourSteps } from "../tour/trainingTourSteps";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useStandardDetailQuery } from "../api/queries";
import { trainingRoutes } from "../trainingRoutes";
import { mapApiToServiceStandardDetail } from "../lib/mapServiceStandardDetail";
import { TrainingServiceStandardDetailView } from "../ui/TrainingServiceStandardDetailView";

const defaultStandardsListSearch = {
  sortBy: undefined as string | undefined,
  sortOrder: undefined as "asc" | "desc" | undefined,
};

export function ServiceStandardDetailPage() {
  const tour = useTrainingTour("training-standard-detail-tour-completed");
  const navigate = useNavigate({ from: "/app/training/standards/$id" });
  const { id } = useParams({ from: "/app/training/standards/$id" });
  const { data, isLoading, error } = useStandardDetailQuery(id);

  const handleBack = () => {
    navigate({
      to: trainingRoutes.standardsList,
      search: defaultStandardsListSearch,
    });
  };

  const handleViewPDF = () => {
    navigate({
      from: "/app/training/standards/$id",
      to: trainingRoutes.standardPdfPreview,
      params: { id },
    });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບມາດຕະຖານບໍລິການ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ມາດຕະຖານບໍລິການທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button type="button" onClick={handleBack} className="gap-2">
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບໄປທີ່ລາຍການ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const standard = mapApiToServiceStandardDetail(data as Record<string, unknown>);

  return (
    <>
      <Header />
      <Main>
        <div className="flex justify-between gap-2">
          <div className="mb-4 flex items-center gap-2" data-tourid="training-standard-detail-page-title">
            <h1 className="font-bold text-2xl tracking-tight">{standard.name}</h1>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={tour.startTour}
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              title="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-4" />
            </Button>
          </div>
          <div
            className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            data-tourid="training-standard-detail-toolbar"
          >
            <Button type="button" variant="ghost" onClick={handleBack} className="w-full gap-2 sm:w-auto">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປທີ່ລາຍການ
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleViewPDF}
              className="w-full gap-2 sm:w-auto"
            >
              <FileText className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>
          </div>
        </div>



        <TrainingServiceStandardDetailView standard={standard} />

        <TrainingTour
          steps={serviceStandardDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
